/**
 * =============================================================================
 * NOTICE:
 * 
 * This module, along with its related sub-modules, is being developed and tested
 * in a central repository at https://github.com/palainteractive/web-geocomply-lib
 * 
 * IMPORTANT:
 * Any modifications directly to these source files will not be accepted unless 
 * they are submitted as a pull request, with adaquate documentation and associated 
 * tests, to the central repository:
 * 	https://github.com/palainteractive/web-geocomply-lib
 * 
 * By adhering to this process, we ensure that all changes are properly reviewed 
 * and integrated, maintaining the integrity and consistency of the codebase.
 * 
 * =============================================================================
 */

import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subject } from 'rxjs';
import { CustomerModel } from '../../models/customer-model';
import { GeoComplyHint, GeoComplyStatus } from '../../interfaces/geo-interface';
import { ConfigService } from '../config/config.service';
import { UserSessionService } from '../user-session/user-session.service';
import { WebservicesService } from '../webservices/webservices.service';
import { EventsService } from '../event/event.service';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';

declare var GCOobee: any;

const coerceInt = (a: string | number) => ("string" === typeof a ? parseInt(a) : a);

interface GeocomplyCertificate {
    expires?: number;
    license?: string;
}

@Injectable({
    providedIn: 'root'
})

export class GeoComplyOobeeService {

    private initConfig: any = {};
    private certificates: Record<string, GeocomplyCertificate> = {}
    private mobileWebClient: any = null;
    private clientapp:any = null;
    private geocomplySubject: Subject<GeoComplyStatus> = new Subject();
    private customer?: CustomerModel;
    private hint?: GeoComplyHint;

    constructor(
        protected config: ConfigService,
        protected platform: Platform,
        protected events: EventsService,
        protected webservices: WebservicesService,
        protected userSession: UserSessionService
    ){
        this.config.isLoaded().then( () => {
            this.initConfig = {
                oobeeUrl: this.config.getConfig().FEATURE_GEOCOMPLY_OOBEE_OOBEEURL,
                license: null
            }
        })

        this.userSession.getCustomer().subscribe((customer) => {
            if (!this.customer || !customer || (customer.customerId !== this.customer.customerId)) {
                this.customer = customer;
            }
        });
    }

    public getGeoStatuses(): Subject<GeoComplyStatus> {
        return this.geocomplySubject;
    }

    public setLicense( license: string) {
        console.log( 'OOBEE: setLicense: ', license);
        this.initConfig.license = license;
    }

    public initializeMobileWeb( clientapp:string):void {
        this.clientapp = clientapp;
    }

    protected actualOobeeInitializer(): Promise<any> {
        return this.getGeoComplyLicense(this.clientapp).then( (licenseData) => {
            this.setLicense( licenseData);
            this.mobileWebClient = GCOobee.createClient(this.initConfig);
            this.mobileWebClient.configure( {
                enablePlayStorePopUp: false,
                autoOpenAppStore: false,
                autoOpenPlayStore: false,
                license: licenseData,
                oobeeUrl: this.initConfig.oobeeUrl
            })
            return this.mobileWebClient;
        })
    }

    public isGeolocationInProgress(){
        return this.mobileWebClient.isGeolocationInProgress();
    }

    public triggerMobileGeoComply( user_id: string, reason: string, license: string,data:any) {
        if (this.customer && this.customer.customerId  && !user_id) {
            user_id = this.customer.customerId.toString();
        }
        this.certificates[this.clientapp] = data;
        let mythis = this;
        const middleCallback = function (data) {
        switch (data.type) {
            case 'LOG':
                console.log(data.message);
                break;
            case 'INTERACTION':
                // Acquire interaction: such as show popup with data.link...
                // Data object includes: {type, message, reason, link}
                // Acquire interaction with data.link:
                // Such as show popup. It should have a timer to push users to click on it.
                // There is a reason field inside the INTERACTION object for clients can
                // detect if they want to use their own message as below:
                // 'app_required': when need to open app to geolocate.
                // 'tlb_require': when need to open TLB to geolocate.
                // 'location_required': when Location permission is not granted.
                // 'precise_location_required': when Precise Location permission is not granted.
                // 'notification_required': when Notification on IOS permission is not granted. (Android no need notification permission anymore)
                if( data.reason === 'app_required') {
                    mythis.geocomplySubject.next( {
                        error_code: 'INTERACTION',
                        error_message: data.message,
                        hint: data.link
                    } as GeoComplyStatus)
                }
                console.log( `OOBEE: INTERACTION: type: ${data.type} message: ${data.message} reason: ${data.reason} link: ${data.link}`);
                break;
            default:
                console.log(`OOBEE: Unhandled ${data.type}: ${data.message}`);
        } };

        const finalCallback = function (err, data) {
            if (err) {
                console.log('OOBEE: Geolocate failed with error code: ' + err.error_code + ' Message: ' + err.error_message);
                mythis.onGeoComplyFailure(err);
            } else {
                mythis.onGeoComplyPacket( data);
                console.log('OOBEE: Geolocate succeeded with data: ' + data);
            }
        };

        var geolocateOptions = {
        userId: user_id,
        reason: reason,
        }

        if( !this.initConfig || !this.initConfig.license) {
            return this.actualOobeeInitializer().then((mobileWebClient) => {
                mobileWebClient.geolocate( geolocateOptions, middleCallback, finalCallback);
            });             
        }

        this.mobileWebClient.setLicense(license);
        return this.mobileWebClient.geolocate( geolocateOptions, middleCallback, finalCallback);
    }

    protected getGeoComplyLicense(clientapp:string): Promise<any> {
        var licensePromise = new Promise<any>((resolve, error) => {
            if( "undefined" === typeof this.certificates[clientapp]) {
                this.certificates[clientapp] = {
                    expires: undefined,
                    license: undefined
                }
            }
            let now = (new Date()).getTime();
            if (!this.certificates[clientapp].expires || (now >= this.certificates[clientapp].expires!)) {
                this.webservices.get('geoComply/license', {clientapp: clientapp}).subscribe((resp: any) => {
                    if (resp.result) {
                        this.certificates[clientapp].license = resp.payload.certificate;
                        if ("undefined" !== typeof resp.payload.expires) {
                            this.certificates[clientapp].expires = resp.payload.expires * 1000;    // convert seconds to milliseconds
                            resolve(this.certificates[clientapp].license);
                        } else {
                            this.certificates[clientapp].expires = 0;        // expire immediately, certificate is no good
                            error('invalid expiry on certificate');
                        }
                    } else {
                        error('invalid response from license server');
                    }
                });
            } else {
                resolve(this.certificates[clientapp].license);
            }
        });
        return licensePromise;
    }

    protected onGeoComplyPacket(data: string) {
        return this.webservices.post('geoComply/data', {
            customer_id: this.customer?.customerId,
            session_token: this.userSession.getSessionToken(),
            enc_data: encodeURIComponent(data),
            clientapp: this.config.getClientApp()
        }).toPromise().then((response: PlatformResponse<any>) => {
            console.log('OOBEE: geo-comply-oobee.ts: geoStatus: ' + JSON.stringify(response));
            this.events.publish('event:geoComply:dismiss');

            let code = coerceInt(response.payload.error_code);
            let timer = coerceInt(response.payload.geolocate_in);

            this.geocomplySubject.next({
                error_code: code,
                error_message: response.payload.error_message,
                error_details: response.payload.error_details,
                geolocate_in: timer,
                troubleshooter_message: response.payload.troubleshooter_message,
                hint: this.hint
            } as GeoComplyStatus);
        }).catch((e: any) => {
            console.warn('OOBEE: geo-comply-oobee.service.ts: EXCEPTION: ', e);
        });
    }

    protected onGeoComplyFailure(err){
        return this.webservices.post('geoComply/data', {
            customer_id: this.customer.customerId,
            session_token: this.userSession.getSessionToken(),
            enc_data: 'undefined',
            clientapp: this.config.getClientApp()
        }).toPromise().then((response: PlatformResponse<any>) => {
            console.log('OOBEE: Failure and Stop: ' + JSON.stringify(response));
            this.events.publish('event:geoComply:dismiss');

            this.geocomplySubject.next({
                error_code: err.error_code,
                hint: this.hint
            } as GeoComplyStatus);
        }).catch((e: any) => {
            console.warn('OOBEE: geo-comply-oobee.service.ts: EXCEPTION: ', e);
        });
    }

}

